import { ChecksumException, FormatException, NotFoundException } from "@zxing/library";
import { type HTMLVisualMediaElement } from "@zxing/browser";
import { env, type Tensor } from "onnxruntime-web";
import {
  calculateResizedDimensions,
  cropImage,
  file2Image,
  imageData2Tensor,
} from "~/features/scanDmc/components/DMCScanPage/DMCScanner/utils";
import { Detector } from "~/features/scanDmc/components/DMCScanPage/DMCScanner/Detector";
import { Decoder } from "~/features/scanDmc/components/DMCScanPage/DMCScanner/Decoder";
import type Exception from "@zxing/library/esm/core/Exception";

type Result = string | null | undefined;
type DecodeContinuouslyCallback = (result: Result, error?: Exception) => void;

export function createDMCScanner(wasmFilePath?: string, modelPath?: string) {
  if (wasmFilePath) {
    // window.ort.env.wasm.wasmPaths = "https://cdn.jsdelivr.net/npm/onnxruntime-web/dist/";
    env.wasm.wasmPaths = wasmFilePath;
  }
  return new DMCScanner(new Detector(modelPath), new Decoder(), 1000);
}

export class DMCScanner {
  /**
   * This will break the loop.
   */
  private _stopContinuousDecode: boolean | undefined = false;

  /**
   * This will break the loop.
   */
  private _stopAsyncDecode: boolean | undefined = false;

  /**
   * Delay time between decode attempts made by the scanner.
   */
  protected _timeBetweenDecodingAttempts = 0;

  protected captureCanvas: HTMLCanvasElement | undefined;
  protected captureCanvasContext: CanvasRenderingContext2D | undefined;
  protected imageElement: HTMLImageElement | undefined;
  protected imageLoadedListener: EventListener | undefined;
  protected stream: MediaStream | undefined;
  protected videoElement: HTMLVideoElement | undefined;
  protected videoCanPlayListener: EventListener | undefined;
  protected videoEndedListener: EventListener | undefined;
  protected videoPlayingEventListener: EventListener | undefined;

  private onReadyPromise;
  private resolveOnReady!: () => void;
  private rejectOnReady!: () => void;

  public constructor(
    private detector: Detector,
    private decoder: Decoder,
    protected timeBetweenScansMillis: number = 500,
  ) {
    this.onReadyPromise = new Promise<void>((resolve, reject) => {
      this.resolveOnReady = resolve;
      this.rejectOnReady = reject;
    });
    this.detector.onReady().then(this.resolveOnReady).catch(this.rejectOnReady);
  }

  public onReady() {
    return this.onReadyPromise;
  }

  public async decodeFromStream(
    stream: MediaStream,
    videoSource: HTMLVideoElement,
    callbackFn: DecodeContinuouslyCallback,
  ) {
    this.reset();

    const video = await this.attachStreamToVideo(stream, videoSource);

    this.decodeContinuously(video, callbackFn);
  }

  public stopAsyncDecode() {
    this._stopAsyncDecode = true;
  }

  public stopContinuousDecode() {
    this._stopContinuousDecode = true;
  }

  async attachStreamToVideo(stream: MediaStream, videoSource: HTMLVideoElement): Promise<HTMLVideoElement> {
    const videoElement = this.prepareVideoElement(videoSource);

    // Older browsers may not have `srcObject`
    videoElement.srcObject = stream;

    this.videoElement = videoElement;
    this.stream = stream;

    await this.playVideoOnLoadAsync(videoElement);

    return videoElement;
  }

  /**
   *
   * @param videoElement
   */
  protected playVideoOnLoadAsync(videoElement: HTMLVideoElement): Promise<void> {
    return new Promise((resolve) => this.playVideoOnLoad(videoElement, () => resolve()));
  }

  /**
   * Binds listeners and callbacks to the videoElement.
   *
   * @param element
   * @param callbackFn
   */
  protected playVideoOnLoad(element: HTMLVideoElement, callbackFn: EventListener): void {
    this.videoEndedListener = () => this.stopStreams();
    this.videoCanPlayListener = () => this.tryPlayVideo(element);

    element.addEventListener("ended", this.videoEndedListener);
    element.addEventListener("canplay", this.videoCanPlayListener);
    element.addEventListener("playing", callbackFn);

    // if canplay was already fired, we won't know when to play, so just give it a try
    this.tryPlayVideo(element);
  }

  /**
   * Checks if the given video element is currently playing.
   */
  isVideoPlaying(video: HTMLVideoElement): boolean {
    return video.currentTime > 0 && !video.paused && !video.ended && video.readyState > 2;
  }

  /**
   * Just tries to play the video and logs any errors.
   * The play call is only made is the video is not already playing.
   */
  async tryPlayVideo(videoElement: HTMLVideoElement): Promise<void> {
    if (this.isVideoPlaying(videoElement)) {
      console.warn("Trying to play video that is already playing.");
      return;
    }

    try {
      await videoElement.play();
    } catch {
      console.warn("It was not possible to play the video.");
    }
  }

  /**
   * Sets a HTMLVideoElement for scanning or creates a new one.
   *
   * @param videoSource The HTMLVideoElement to be set.
   */
  public prepareVideoElement(videoSource: HTMLVideoElement): HTMLVideoElement {
    let videoElement: HTMLVideoElement;

    if (!videoSource && typeof document !== "undefined") {
      videoElement = document.createElement("video");
      videoElement.width = 200;
      videoElement.height = 200;
    }

    if (videoSource instanceof HTMLVideoElement) {
      videoElement = videoSource;
    }

    // Needed for iOS 11
    videoElement!.setAttribute("autoplay", "true");
    videoElement!.setAttribute("muted", "true");
    videoElement!.setAttribute("playsinline", "true");

    return videoElement!;
  }

  public async decodeOnceFromConstraints(
    constraints: MediaStreamConstraints,
    videoSource: HTMLVideoElement,
  ): Promise<Result> {
    const stream = await navigator.mediaDevices.getUserMedia(constraints);

    return await this.decodeOnceFromStream(stream, videoSource);
  }

  public async decodeOnceFromStream(stream: MediaStream, videoSource: HTMLVideoElement): Promise<Result> {
    this.reset();

    const video = await this.attachStreamToVideo(stream, videoSource);
    return await this.decodeOnce(video);
  }

  public decodeOnce(
    element: HTMLVisualMediaElement,
    retryIfNotFound = true,
    retryIfChecksumOrFormatError = true,
  ): Promise<string | null | undefined> {
    this._stopAsyncDecode = false;

    const loop = async (
      resolve: (value: string | null | undefined) => void,
      reject: (reason?: unknown) => void,
    ): Promise<void> => {
      if (this._stopAsyncDecode) {
        reject(new NotFoundException("Video stream has ended before any code could be detected."));
        this._stopAsyncDecode = undefined;
        return;
      }

      try {
        const result = await this.decode(element);
        resolve(result);
      } catch (e) {
        const ifNotFound = retryIfNotFound && e instanceof NotFoundException;
        const isChecksumOrFormatError = e instanceof ChecksumException || e instanceof FormatException;
        const ifChecksumOrFormat = isChecksumOrFormatError && retryIfChecksumOrFormatError;

        if (ifNotFound || ifChecksumOrFormat) {
          // trying again
          window.setTimeout(loop, this._timeBetweenDecodingAttempts, resolve, reject);
          return;
        }

        reject(e);
      }
    };

    return new Promise((resolve, reject) => loop(resolve, reject));
  }

  public decodeContinuously(element: HTMLVideoElement, callbackFn: DecodeContinuouslyCallback): void {
    this._stopContinuousDecode = false;

    const loop = async () => {
      if (this._stopContinuousDecode) {
        this._stopContinuousDecode = undefined;
        return;
      }

      try {
        const result = await this.decode(element);
        callbackFn(result, undefined);
        window.setTimeout(loop, this.timeBetweenScansMillis);
      } catch (e) {
        callbackFn(null, e as Exception);

        const isChecksumOrFormatError = e instanceof ChecksumException || e instanceof FormatException;
        const isNotFound = e instanceof NotFoundException;

        if (isChecksumOrFormatError || isNotFound) {
          // trying again
          window.setTimeout(loop, this._timeBetweenDecodingAttempts);
        }
      }
    };

    loop();
  }

  public decode(mediaElement: HTMLVisualMediaElement) {
    this.getCaptureCanvasContext(mediaElement);
    if (mediaElement instanceof HTMLVideoElement) {
      this.drawFrameOnCanvas(<HTMLVideoElement>mediaElement);
    } else {
      this.drawImageOnCanvas(<HTMLImageElement>mediaElement);
    }
    const canvas = this.getCaptureCanvas(mediaElement);
    const preprocessed = this.preprocessImage(canvas);

    return this.decodePreprocessed(preprocessed);
  }

  private async decodePreprocessed(result: PreprocessResult) {
    const detectionResult = await this.detector.detect(result.tensor, {
      width: result.sourceWidth,
      height: result.sourceHeight,
    });

    if (!detectionResult) {
      throw new NotFoundException("No detection result.");
    }

    const cropCanvas = cropImage(result.originalImage, detectionResult);

    const decodedResult = await this.decoder.decode(cropCanvas);

    if (!decodedResult) {
      throw new FormatException("No decoded result.");
    }

    return decodedResult;
  }

  protected getCaptureCanvasContext(mediaElement?: HTMLVisualMediaElement) {
    if (!this.captureCanvasContext) {
      const elem = this.getCaptureCanvas(mediaElement);
      let ctx;
      try {
        ctx = elem.getContext("2d", { willReadFrequently: true }) as CanvasRenderingContext2D;
      } catch (e) {
        ctx = elem.getContext("2d");
      }
      this.captureCanvasContext = ctx!;
    }

    return this.captureCanvasContext;
  }

  /**
   *
   */
  protected getCaptureCanvas(mediaElement?: HTMLVisualMediaElement): HTMLCanvasElement {
    if (!this.captureCanvas) {
      this.captureCanvas = this.createCaptureCanvas(mediaElement);
    }

    return this.captureCanvas;
  }

  /**
   * Overwriting this allows you to manipulate the next frame in anyway you want before decode.
   */
  public drawFrameOnCanvas(
    srcElement: HTMLVideoElement,
    dimensions = {
      sx: 0,
      sy: 0,
      sWidth: srcElement.videoWidth,
      sHeight: srcElement.videoHeight,
      dx: 0,
      dy: 0,
      dWidth: srcElement.videoWidth,
      dHeight: srcElement.videoHeight,
    },
    canvasElementContext = this.captureCanvasContext!,
  ) {
    canvasElementContext.drawImage(
      srcElement,
      dimensions.sx,
      dimensions.sy,
      dimensions.sWidth,
      dimensions.sHeight,
      dimensions.dx,
      dimensions.dy,
      dimensions.dWidth,
      dimensions.dHeight,
    );
  }

  /**
   * Ovewriting this allows you to manipulate the snapshot image in anyway you want before decode.
   */
  public drawImageOnCanvas(
    srcElement: HTMLImageElement,
    dimensions = {
      sx: 0,
      sy: 0,
      sWidth: srcElement.naturalWidth,
      sHeight: srcElement.naturalHeight,
      dx: 0,
      dy: 0,
      dWidth: srcElement.naturalWidth,
      dHeight: srcElement.naturalHeight,
    },
    canvasElementContext = this.captureCanvasContext!,
  ) {
    canvasElementContext.drawImage(
      srcElement,
      dimensions.sx,
      dimensions.sy,
      dimensions.sWidth,
      dimensions.sHeight,
      dimensions.dx,
      dimensions.dy,
      dimensions.dWidth,
      dimensions.dHeight,
    );
  }

  /**
   * 🖌 Prepares the canvas for capture and scan frames.
   */
  public createCaptureCanvas(mediaElement?: HTMLVisualMediaElement): HTMLCanvasElement {
    const canvasElement = document.createElement("canvas");

    let width: number;
    let height: number;

    if (typeof mediaElement !== "undefined") {
      if (mediaElement instanceof HTMLVideoElement) {
        width = mediaElement.videoWidth;
        height = mediaElement.videoHeight;
      } else if (mediaElement instanceof HTMLImageElement) {
        width = mediaElement.naturalWidth || mediaElement.width;
        height = mediaElement.naturalHeight || mediaElement.height;
      }
    }

    canvasElement.style.width = width! + "px";
    canvasElement.style.height = height! + "px";
    canvasElement.width = width!;
    canvasElement.height = height!;

    return canvasElement;
  }

  /**
   * Stops the continuous scan and cleans the stream.
   */
  protected stopStreams(): void {
    if (this.stream) {
      this.stream.getVideoTracks().forEach((t) => t.stop());
      this.stream = undefined;
    }
    if (this._stopAsyncDecode === false) {
      this.stopAsyncDecode();
    }
    if (this._stopContinuousDecode === false) {
      this.stopContinuousDecode();
    }
  }

  public reset() {
    // stops the camera, preview and scan 🔴

    this.stopStreams();

    // clean and forget about HTML elements

    this._destroyVideoElement();
    this._destroyImageElement();
    this._destroyCaptureCanvas();
  }

  private _destroyVideoElement(): void {
    if (!this.videoElement) {
      return;
    }

    // first gives freedon to the element 🕊

    if (typeof this.videoEndedListener !== "undefined") {
      this.videoElement.removeEventListener("ended", this.videoEndedListener);
    }

    if (typeof this.videoPlayingEventListener !== "undefined") {
      this.videoElement.removeEventListener("playing", this.videoPlayingEventListener);
    }

    if (typeof this.videoCanPlayListener !== "undefined") {
      this.videoElement.removeEventListener("loadedmetadata", this.videoCanPlayListener);
    }

    // then forgets about that element 😢

    this.cleanVideoSource(this.videoElement);

    this.videoElement = undefined;
  }

  private _destroyImageElement(): void {
    if (!this.imageElement) {
      return;
    }

    // first gives freedom to the element 🕊

    if (undefined !== this.imageLoadedListener) {
      this.imageElement.removeEventListener("load", this.imageLoadedListener);
    }

    // then forget about that element 😢

    this.imageElement.src = "";
    this.imageElement.removeAttribute("src");
    this.imageElement = undefined;
  }

  /**
   * Cleans canvas references 🖌
   */
  private _destroyCaptureCanvas(): void {
    // then forget about that element 😢

    this.captureCanvasContext = undefined;
    this.captureCanvas = undefined;
  }
  /**
   * Unbinds a HTML video src property.
   *
   * @param videoElement
   */
  private cleanVideoSource(videoElement: HTMLVideoElement): void {
    try {
      videoElement.srcObject = null;
    } catch (err) {
      videoElement.src = "";
    }

    videoElement.removeAttribute("src");
  }

  private preprocessImage(image: HTMLImageElement | HTMLCanvasElement): PreprocessResult {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d")!;

    const sizeData = calculateResizedDimensions(
      { width: image.width, height: image.height },
      { width: MODEL_INPUT_WIDTH, height: MODEL_INPUT_HEIGHT },
    );

    // Set the canvas dimensions to the desired dimensions
    canvas.width = MODEL_INPUT_WIDTH;
    canvas.height = MODEL_INPUT_HEIGHT;
    // Draw the image to the canvas at the new size
    ctx.drawImage(image, sizeData.offsetX, sizeData.offsetY, sizeData.width, sizeData.height);

    const imageData = ctx.getImageData(0, 0, MODEL_INPUT_WIDTH, MODEL_INPUT_HEIGHT);

    return {
      sourceHeight: image.height,
      sourceWidth: image.width,
      resizedHeight: canvas.height,
      resizedWidth: canvas.width,
      offsetX: sizeData.offsetX,
      offsetY: sizeData.offsetY,
      originalImage: image,
      tensor: imageData2Tensor(imageData),
    };
  }

  async decodeFromFile(file: File) {
    const image = await file2Image(file);

    const preprocessed = this.preprocessImage(image);
    return this.decodePreprocessed(preprocessed);
  }
}

const MODEL_INPUT_WIDTH = 640;
const MODEL_INPUT_HEIGHT = 640;

interface PreprocessResult {
  sourceHeight: number;
  sourceWidth: number;
  resizedHeight: number;
  resizedWidth: number;
  offsetX: number;
  offsetY: number;
  originalImage: HTMLImageElement | HTMLCanvasElement;
  tensor: Tensor;
}
